import { Box, IconButton } from '@mui/material';
import { useState } from 'react';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import PdfHighlighter from '@src/components/utilities/PDFHighlighter';
import MigrationSuggestionDTO from '@src/adapters/dto/migration/MigrationSuggestionDTO';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

interface Props {
  pdfURL: string | undefined;
  suggestions: MigrationSuggestionDTO[];
  highlightItemId: string;
}

function PDFPreviewSection({ pdfURL, suggestions, highlightItemId }: Props) {
  const [rotation, setRotation] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [zoomLevel, setZoomLevel] = useState<number>(1);

  const rotate = (left: boolean) => {
    setRotation(
      (prevRotation: number) => (prevRotation - (left ? 90 : -90)) % 360
    );
  };

  const handlePageChange = (next: boolean) => {
    const newPageNumber = next ? pageNumber + 1 : pageNumber - 1;
    if (newPageNumber > 0 && newPageNumber <= numberOfPages) {
      setPageNumber(newPageNumber);
    }
  };

  const handleZoom = (increase: boolean) => {
    if (increase) {
      setZoomLevel((prev) => prev + 0.5);
    } else {
      setZoomLevel((prev) => (prev - 0.5 < 0.5 ? 0.5 : prev - 0.5));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        width: '100%',
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
          <Box>
            <RotateLeftIcon
              sx={(theme) => ({
                width: '43px',
                height: '43px',
                cursor: 'pointer',
                color: theme.palette.primary.main,
              })}
              onClick={() => rotate(true)}
            />
            <RotateRightIcon
              sx={(theme) => ({
                width: '43px',
                height: '43px',
                cursor: 'pointer',
                color: theme.palette.primary.main,
              })}
              onClick={() => rotate(false)}
            />
          </Box>
          <Box>
            <ZoomOutIcon
              sx={(theme) => ({
                width: '43px',
                height: '43px',
                cursor: 'pointer',
                color: theme.palette.primary.main,
              })}
              onClick={() => handleZoom(false)}
            />
            <ZoomInIcon
              sx={(theme) => ({
                width: '43px',
                height: '43px',
                cursor: 'pointer',
                color: theme.palette.primary.main,
              })}
              onClick={() => handleZoom(true)}
            />
          </Box>
          {numberOfPages > 1 && (
            <Box>
              {pageNumber}/{numberOfPages}
            </Box>
          )}
          {numberOfPages > 1 && (
            <Box>
              <IconButton
                onClick={() => handlePageChange(false)}
                disabled={pageNumber <= 1}>
                <NavigateBeforeIcon />
              </IconButton>
              <IconButton
                onClick={() => handlePageChange(true)}
                disabled={pageNumber >= numberOfPages}>
                <NavigateNextIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
      <PdfHighlighter
        pdfUrl={pdfURL}
        rotation={rotation}
        pageNumber={pageNumber}
        suggestions={suggestions}
        highlightItemId={highlightItemId}
        setNumberOfPages={setNumberOfPages}
        zoomLevel={zoomLevel}
      />
    </Box>
  );
}

export default PDFPreviewSection;
